import React, { useCallback } from 'react';
import { Linking } from 'react-native';
import {
  Button, HStack, Icon, Text,
} from '@gluestack-ui/themed';
import { Mail, MessageCircle, Phone } from 'lucide-react-native';
import { useTranslation } from 'react-i18next';

export type PersonPreviewReach = {
  email?: string | null;
  phone?: string | null;
  whatsapp?: string | null;
};

type PersonReachChipsProps = {
  reach: PersonPreviewReach;
  interactive?: boolean;
};

function telUrl(raw: string): string {
  const trimmed = raw.trim();
  const digitsAndPlus = trimmed.replace(/[^\d+]/g, '');
  return `tel:${digitsAndPlus}`;
}

function whatsappUrl(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  return `https://wa.me/${digits}`;
}

/**
 * Reach chips for accepted connections (or muted preview on Profile).
 * Visible label is the stored contact (email / phone / WhatsApp number), not a generic word.
 */
export function PersonReachChips({ reach, interactive = true }: PersonReachChipsProps) {
  const { t } = useTranslation();
  const chips: Array<{
    key: string;
    label: string;
    accessibilityLabel: string;
    icon: typeof Mail;
    url: string;
  }> = [];

  if (reach.email) {
    chips.push({
      key: 'email',
      label: reach.email,
      accessibilityLabel: `${t('people.reach.email')}: ${reach.email}`,
      icon: Mail,
      url: `mailto:${reach.email}`,
    });
  }
  if (reach.phone) {
    chips.push({
      key: 'phone',
      label: reach.phone,
      accessibilityLabel: `${t('people.reach.call')}: ${reach.phone}`,
      icon: Phone,
      url: telUrl(reach.phone),
    });
  }
  if (reach.whatsapp) {
    chips.push({
      key: 'whatsapp',
      label: reach.whatsapp,
      accessibilityLabel: `${t('people.reach.whatsapp')}: ${reach.whatsapp}`,
      icon: MessageCircle,
      url: whatsappUrl(reach.whatsapp),
    });
  }

  const handlePress = useCallback((url: string) => {
    if (!interactive) return;
    Linking.openURL(url).catch(() => {});
  }, [interactive]);

  if (chips.length === 0) return null;

  return (
    <HStack space="sm" flexWrap="wrap">
      {chips.map(({
        key, label, accessibilityLabel, icon, url,
      }) => (
        <Button
          key={key}
          size="xs"
          variant="outline"
          borderColor="$borderMedium"
          isDisabled={!interactive}
          accessibilityLabel={accessibilityLabel}
          testID={`reach-chip-${key}`}
          onPress={() => handlePress(url)}
        >
          <Icon as={icon} size="xs" color="$textHint" mr="$1" />
          <Text size="xs" color="$textHint">{label}</Text>
        </Button>
      ))}
    </HStack>
  );
}
